<template>
  <nav
    v-if="showFooter"
    class="app-footer"
    :class="isMobile ? 'app-footer--fixed' : 'app-footer--absolute'"
    aria-label="Main navigation"
  >
    <div class="app-footer__inner">
      <component
        v-for="item in navItems"
        :key="item.id"
        :is="item.external ? 'a' : 'button'"
        :href="item.external ? item.href : undefined"
        :type="item.external ? undefined : 'button'"
        class="app-footer__tab"
        :class="{
          'app-footer__tab--active': !item.external && isActive(item),
          'app-footer__tab--center': item.center,
        }"
        :target="item.external ? '_blank' : undefined"
        :rel="item.external ? 'noopener noreferrer' : undefined"
        @click="!item.external && goTo(item)"
      >
        <span
          class="app-footer__icon-wrap"
          :class="{ 'app-footer__icon-wrap--center': item.center }"
        >
          <i :class="item.icon" aria-hidden="true"></i>
        </span>
        <span class="app-footer__label">{{ $t(item.labelKey) }}</span>
      </component>
    </div>
  </nav>
</template>

<script>
export default {
  data() {
    return {
      isMobile: false,
      navItems: [
        {
          id: 'home',
          icon: 'fas fa-home',
          labelKey: 'footer.home',
          route: '/home',
          match: ['/home', '/'],
        },
        {
          id: 'promotion',
          icon: 'fas fa-gift',
          labelKey: 'footer.promotion',
          route: '/promotion',
          match: ['/promotion'],
        },
        {
          id: 'deposit',
          icon: 'fas fa-wallet',
          labelKey: 'setting.deposit',
          route: '/deposit',
          match: ['/deposit'],
          center: true,
        },
        {
          id: 'withdraw',
          icon: 'fas fa-hand-holding-usd',
          labelKey: 'setting.withdraw',
          route: '/withdraw',
          match: ['/withdraw'],
        },
        {
          id: 'profile',
          icon: 'fas fa-user',
          labelKey: 'footer.profile',
          route: '/setting',
          match: ['/setting'],
        },
      ],
    };
  },
  computed: {
    showFooter() {
      const path = this.$route.path;
      return path !== '/maung-fb' && path !== '/body-fb' && path !== '/iframeURL';
    },
  },
  methods: {
    onResize() {
      this.isMobile = window.innerWidth < 768;
    },
    isActive(item) {
      if (!item.match) return false;
      return item.match.some((path) => {
        if (path === '/') return this.$route.path === '/';
        return this.$route.path === path || this.$route.path.startsWith(`${path}/`);
      });
    },
    goTo(item) {
      if (item.route && this.$route.path !== item.route) {
        this.$router.push(item.route);
      }
    },
  },
  mounted() {
    this.isMobile = window.innerWidth < 768;
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
};
</script>

<style scoped>
.app-footer {
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  padding-bottom: env(safe-area-inset-bottom, 0);
  background: rgba(8, 8, 8, 0.92);
  border-top: 1px solid var(--border-gold);
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.app-footer--fixed {
  position: fixed;
}

.app-footer--absolute {
  position: absolute;
}

.app-footer__inner {
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  max-width: 100%;
  margin: 0 auto;
  padding: 0.15rem 0.35rem 0.35rem;
  min-height: 3.75rem;
}

.app-footer__tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 0.15rem;
  min-width: 0;
  padding: 0.15rem 0.1rem;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  text-decoration: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: color 0.2s ease, transform 0.15s ease;
}

.app-footer__tab:active {
  transform: scale(0.94);
}

.app-footer__tab--active {
  color: var(--text-gold);
}

.app-footer__tab--active .app-footer__icon-wrap {
  background: rgba(207, 167, 87, 0.15);
  border-color: rgba(207, 167, 87, 0.35);
}

.app-footer__tab--active .app-footer__icon-wrap i {
  color: var(--text-gold);
}

.app-footer__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 10px;
  border: 1px solid transparent;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.app-footer__icon-wrap i {
  font-size: 1rem;
  color: #d4d4d4;
  transition: color 0.2s ease;
}

.app-footer__tab--center {
  position: relative;
  z-index: 2;
}

.app-footer__icon-wrap--center {
  width: 2.85rem;
  height: 2.85rem;
  margin-top: -1.1rem;
  border-radius: 50%;
  background: var(--gradient-primary);
  border: 2px solid #fae68d;
  box-shadow:
    0 4px 18px rgba(207, 167, 87, 0.55),
    0 0 0 3px rgba(8, 8, 8, 0.95),
    0 0 20px rgba(250, 230, 141, 0.25);
}

.app-footer__icon-wrap--center i {
  font-size: 1.2rem;
  color: #1a1a1a;
  filter: drop-shadow(0 1px 0 rgba(255, 255, 255, 0.25));
}

.app-footer__tab--center.app-footer__tab--active .app-footer__icon-wrap--center,
.app-footer__tab--center .app-footer__icon-wrap--center {
  box-shadow:
    0 6px 22px rgba(207, 167, 87, 0.65),
    0 0 0 3px rgba(8, 8, 8, 0.95),
    0 0 24px rgba(250, 230, 141, 0.35);
}

.app-footer__tab--center .app-footer__label {
  color: #fae68d;
  font-size: 0.68rem;
  font-weight: 800;
  margin-top: 0.1rem;
  text-shadow: 0 0 10px rgba(250, 230, 141, 0.4);
}

.app-footer__label {
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
  max-width: 5rem;
  color: #e8e8e8;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  letter-spacing: 0.03em;
}

.app-footer__tab--active .app-footer__label {
  color: var(--text-gold);
  text-shadow: 0 0 8px rgba(250, 230, 141, 0.35);
}

@media (min-width: 768px) {
  .app-footer__inner {
    padding: 0.3rem 1rem 0.4rem;
    min-height: 3.75rem;
  }

  .app-footer__label {
    font-size: 0.72rem;
    max-width: 5.5rem;
  }

  .app-footer__tab--center .app-footer__label {
    font-size: 0.74rem;
  }
}
</style>
